import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addSurplusSale, updateSurplusSale, reset } from "@/state/surplusSales/surplusSales.slice";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";

export default function SurplusSalesDialog({ open, onOpenChange, surplusSale = null, onSuccess }) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { loading, success, error } = useSelector((state) => state.surplusSales);

  const [formData, setFormData] = useState({
    amount: "",
    reason: "",
    sales_date: format(new Date(), "yyyy-MM-dd"),
    notes: "",
  });

  // Populate form when editing
  useEffect(() => {
    if (surplusSale) {
      setFormData({
        amount: surplusSale.amount || "",
        reason: surplusSale.reason || "",
        sales_date: surplusSale.sales_date ? format(new Date(surplusSale.sales_date), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
        notes: surplusSale.notes || "",
      });
    } else {
      setFormData({
        amount: "",
        reason: "",
        sales_date: format(new Date(), "yyyy-MM-dd"),
        notes: "",
      });
    }
  }, [surplusSale, open]);

  // Handle success/error
  useEffect(() => {
    if (success) {
      toast({ title: "Success", description: surplusSale ? "Surplus sale updated successfully" : "Surplus sale recorded successfully" });
      dispatch(reset());
      setFormData({
        amount: "",
        reason: "",
        sales_date: format(new Date(), "yyyy-MM-dd"),
        notes: "",
      });
      onOpenChange(false);
      if (onSuccess) onSuccess();
    }
    if (error) {
      toast({ variant: "destructive", title: "Error", description: error });
      dispatch(reset());
    }
  }, [success, error, dispatch, toast, onOpenChange, surplusSale, onSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.amount || !formData.reason || !formData.sales_date) {
      toast({ variant: "destructive", title: "Error", description: "Amount, reason, and sales date are required" });
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      toast({ variant: "destructive", title: "Error", description: "Amount must be greater than 0" });
      return;
    }

    try {
      if (surplusSale) {
        await dispatch(updateSurplusSale({ id: surplusSale.id, data: formData })).unwrap();
      } else {
        await dispatch(addSurplusSale(formData)).unwrap();
      }
    } catch (err) {
      // Error handled in useEffect
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{surplusSale ? "Edit Surplus Sale" : "Record Surplus Sale"}</DialogTitle>
          <DialogDescription>
            {surplusSale
              ? "Update the surplus sale record with timestamp and reason."
              : "Record additional revenue for a particular sales date with a reason."
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="sales_date">Sales Date *</Label>
            <Input
              id="sales_date"
              type="date"
              value={formData.sales_date}
              onChange={(e) => setFormData({ ...formData, sales_date: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Amount *</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason *</Label>
            <Textarea
              id="reason"
              placeholder="Explain the reason for this surplus sale..."
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              rows={3}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any additional notes..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {surplusSale ? "Updating..." : "Recording..."}
                </>
              ) : (
                surplusSale ? "Update" : "Record Surplus Sale"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
